import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import { Color } from '@tiptap/extension-color';
import { Emoji, shortcodeToEmoji } from '@tiptap/extension-emoji';
import { Highlight } from '@tiptap/extension-highlight';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { TextStyle } from '@tiptap/extension-text-style';
import { Typography } from '@tiptap/extension-typography';
import { mergeAttributes, ReactNodeViewRenderer } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { common, createLowlight } from 'lowlight';
import { CodeBlockNodeView } from './code-block-node-view';
import { buildEmojiSuggestion } from './emoji/suggestion';
import { buildTableExtensions } from './table/table-kit';

const lowlight = createLowlight(common);

const CodeBlockWithNodeView = CodeBlockLowlight.extend({
  addNodeView() {
    return ReactNodeViewRenderer(CodeBlockNodeView);
  },
});

// Always render Unicode; skip the upstream img fallback.
const EmojiUnicodeOnly = Emoji.extend({
  renderHTML({ HTMLAttributes, node }) {
    const emojiItem = shortcodeToEmoji(node.attrs.name, this.options.emojis);
    const attributes = mergeAttributes(HTMLAttributes, this.options.HTMLAttributes, { 'data-type': this.name });
    if (emojiItem?.emoji) {
      return ['span', attributes, emojiItem.emoji];
    }
    return ['span', attributes, `:${node.attrs.name}:`];
  },
});

export interface BuildExtensionsOptions {
  placeholder?: string;
  /**
   * Whether the resulting extension list is for an editable editor (true) or the viewer
   * (false). Defaults to true. Affects `Link.openOnClick`: in the editor clicking a link
   * positions the cursor; in the viewer clicks should navigate.
   */
  editable?: boolean;
}

export const buildBaseExtensions = ({ placeholder, editable = true }: BuildExtensionsOptions = {}) => [
  // Disable the StarterKit extensions we replace with custom configs below.
  StarterKit.configure({
    codeBlock: false,
    link: false,
    dropcursor: {
      color: 'var(--color-primary-3)',
      width: 2,
    },
  }),
  Link.configure({
    openOnClick: !editable,
    defaultProtocol: 'https',
    autolink: true,
    linkOnPaste: true,
    HTMLAttributes: {
      rel: 'noopener noreferrer nofollow',
      target: '_blank',
      spellcheck: 'false',
    },
  }),
  Image.configure({
    inline: false,
    allowBase64: true,
    resize: {
      enabled: true,
      directions: ['right'],
      minWidth: 50,
      minHeight: 50,
      alwaysPreserveAspectRatio: true,
    },
  }),
  Subscript,
  Superscript,
  TextStyle,
  Color,
  Highlight.configure({ multicolor: true }),
  EmojiUnicodeOnly.configure({
    suggestion: buildEmojiSuggestion(),
  }),
  Typography,
  CodeBlockWithNodeView.configure({ lowlight }),
  Placeholder.configure({
    placeholder: placeholder ?? '',
    showOnlyWhenEditable: true,
    showOnlyCurrent: false,
  }),
  ...buildTableExtensions(),
];
